import { useEffect, useMemo, useState } from 'react'
import Modal from '../../components/common/Modal'
import ReadOnlyGuard from '../../components/common/ReadOnlyGuard'
import {
    ADVICE,
    CUSTOM_VALUE,
    MATERIAL,
    buildCatalogGroups,
    findCatalogOption,
    catalogValueForMaterial,
    materialKindOf,
} from '../../helpers/materialsHelpers/materialCatalog'
import filterCatalogGroups from '../../helpers/materialsHelpers/filterCatalogGroups'
import materialFactorQuestion from '../../helpers/materialsHelpers/materialFactorQuestion'
import { interventionOptions, interventionNameFor } from '../../helpers/materialsHelpers/interventionOptions'
import { defaultForMaterial } from '../../helpers/materialsHelpers/materialInterventionDefaults'
import useInterventieLibrary from '../../hooks/useInterventieLibrary'

const UNIT_OPTIONS = ['stuks', 'meter', 'liter', 'm²', 'kg', 'rol', 'doos', 'pak', 'strip', 'koker']
const UNIT_DATALIST_ID = 'materials-unit-options'
const MATERIAL_SELECT_ID = 'materials-material-select'
const ADVICE_SELECT_ID = 'materials-advice-select'
const OWN_NAME_ID = 'materials-own-name'
const ARTICLE_NUMBER_ID = 'materials-article-number'

// Een naam die niet (meer) in de keuzelijst staat. Materialen van voor de
// keuzelijst dragen zelf getypte namen; die krijgen hun eigen optie zodat het
// veld bij bewerken niet leegloopt en de naam niet stilzwijgend verdwijnt.
const EXISTING_PREFIX = 'bestaand:'

const emptyForm = {
    Kind: MATERIAL,
    Title: '',
    // De naam die de organisatie zelf aan dit product geeft, naast de
    // standaardnaam in Title. Leeg betekent: gebruik de standaardnaam.
    DisplayTitle: '',
    ArticleNumber: '',
    // 'Staat niet in de lijst'. Alleen formulierstate: in Firestore is dit
    // gewoon een materiaal zonder Title.
    Custom: false,
    // Alleen een advies draagt zijn code zelf; een materiaal vindt zijn
    // interventie via zijn naam.
    InterventionCode: '',
    Description: '',
    Unit: '',
    QuantityInStock: 0,
    LowStockThreshold: 0,
    Category: '',
    ItemPrice: 0,
    // Deliberately '' and not 0. An empty conversion factor means "nobody has
    // filled this in", which keeps the material on status needs-factor. A 0
    // would claim the material is worth zero registration units — a measured
    // fact rather than a blank, and it would silently zero out its CO2.
    UnitsPerItem: '',
}

/**
 * De editor voor een regel in de materialenlijst: een materiaal of een
 * coachadvies.
 *
 * De SOORT komt uit een vaste keuzelijst: die bepaalt welke interventie uit de
 * meetstandaard erachter zit. Vrij typen in dat veld leverde namen op die
 * nergens op aansloten en dus nooit meetelden (bug #119). De eigen naam van de
 * organisatie staat daarom in een apart veld ernaast, 'Eigen naam'
 * (`DisplayTitle`), samen met 'Productnummer' (`ArticleNumber`) — zo kan
 * hernoemen de CO2-koppeling niet raken. Staat een product helemaal niet in de
 * lijst, dan is er de optie 'Staat niet in de lijst': `Title` blijft dan leeg en
 * 'Hoort bij' is de enige koppeling.
 * AI-CONTEXT: See docs/features/materials-own-name-and-article-number.md
 *
 * De coachadviezen komen rechtstreeks uit diezelfde meetstandaard.
 *
 * TWEE KEUZELIJSTEN, GEEN ENKELE. Materialen en adviezen stonden onder eigen
 * koppen in een lijst; met de hele meetstandaard erin werd die lijst te lang om
 * in te zoeken. Ze staan nu in twee eigen velden. Een regel blijft precies een
 * van de twee: kiezen in het ene veld leegt het andere.
 *
 * @param {object} props
 * @param {boolean} props.modalOpen - Of de modal getoond wordt
 * @param {(open: boolean) => void} props.setModalOpen - Sluit de modal
 * @param {object|null} props.material - Het te bewerken Materials-document, of null bij toevoegen
 * @param {(form: object) => void} props.onSave - Krijgt het ingevulde formulier
 */
const MaterialsModal = ({ modalOpen, setModalOpen, material, onSave }) => {
    const [form, setForm] = useState(emptyForm)
    // Elk keuzeveld heeft zijn eigen zoekterm: zoeken in de materialen mag de
    // advieslijst niet inkorten.
    const [materialSearch, setMaterialSearch] = useState('')
    const [adviceSearch, setAdviceSearch] = useState('')
    // Pas na een mislukte poging klagen. Een melding die er meteen staat leest
    // als een fout die je nog moet maken.
    const [triedToSave, setTriedToSave] = useState(false)
    const { library, error } = useInterventieLibrary()

    useEffect(() => {
        if (!modalOpen) return

        setMaterialSearch('')
        setAdviceSearch('')
        setTriedToSave(false)

        setForm(material ? {
            Kind: materialKindOf(material),
            Title: material.Title || '',
            DisplayTitle: material.DisplayTitle || '',
            ArticleNumber: material.ArticleNumber || '',
            // Geen standaardnaam maar wel een eigen naam: dit is met 'Staat niet
            // in de lijst' aangemaakt.
            Custom: !material.Title && !!material.DisplayTitle,
            InterventionCode: material.InterventionCode || '',
            Description: material.Description || '',
            Unit: material.Unit || '',
            QuantityInStock: material.QuantityInStock ?? 0,
            LowStockThreshold: material.LowStockThreshold ?? 0,
            Category: material.Category || '',
            ItemPrice: material.ItemPrice ?? 0,
            UnitsPerItem: material.UnitsPerItem ?? '',
        } : emptyForm)
    }, [modalOpen, material])

    // De adviezen hangen aan de opgehaalde bibliotheek; de materialen niet. Bij
    // een onbereikbare standaard blijft de materiaalkant dus gewoon werken.
    const catalogGroups = useMemo(() => buildCatalogGroups(library), [library])
    const materialGroups = useMemo(
        () => catalogGroups.filter((group) => group.kind === MATERIAL),
        [catalogGroups]
    )
    const adviceGroups = useMemo(
        () => catalogGroups.filter((group) => group.kind === ADVICE),
        [catalogGroups]
    )

    const isAdviceForm = form.Kind === ADVICE

    const update = (field) => (e) => {
        const raw = e.target.value
        // Keep numeric fields as '' while the user is clearing them, so backspace
        // actually empties the input instead of snapping back to 0. Coerced to a
        // number in `confirm`.
        const value = field === 'QuantityInStock' || field === 'LowStockThreshold'
            || field === 'ItemPrice' || field === 'UnitsPerItem'
            ? (raw === '' ? '' : Number(raw))
            : raw
        setForm((prev) => ({ ...prev, [field]: value }))
    }

    // De optiewaarde die het formulier op dit moment voorstelt. Een naam die
    // niet in de catalogus staat krijgt zijn eigen waarde, zodat het veld toont
    // wat er is opgeslagen in plaats van leeg te staan.
    const catalogValue = catalogValueForMaterial(form)
    const selectedOption = findCatalogOption(catalogGroups, catalogValue)
    const existingValue = !selectedOption && form.Title ? `${EXISTING_PREFIX}${form.Title}` : ''
    const selectValue = form.Custom
        ? CUSTOM_VALUE
        : (selectedOption ? catalogValue : existingValue)

    // De keuze staat in het veld van zijn eigen soort; het andere veld staat
    // leeg. Zo is te zien dat een regel een materiaal OF een advies is.
    const materialValue = isAdviceForm ? '' : selectValue
    const adviceValue = isAdviceForm ? selectValue : ''

    // De keuzelijst toont alleen wat op de zoekterm matcht; de gekozen optie
    // blijft er hoe dan ook in staan, anders rendert het veld leeg.
    const visibleMaterialGroups = useMemo(
        () => filterCatalogGroups(materialGroups, materialSearch, materialValue),
        [materialGroups, materialSearch, materialValue]
    )
    const visibleAdviceGroups = useMemo(
        () => filterCatalogGroups(adviceGroups, adviceSearch, adviceValue),
        [adviceGroups, adviceSearch, adviceValue]
    )

    const noMaterialMatches = materialSearch.trim() !== '' && visibleMaterialGroups.length === 0
    const noAdviceMatches = adviceSearch.trim() !== '' && visibleAdviceGroups.length === 0

    // Zonder bereikbare meetstandaard zijn er geen adviezen om uit te kiezen.
    // Het veld blijft staan (zodat het niet lijkt alsof adviezen niet bestaan)
    // maar valt dicht — behalve als je een bestaand advies aan het bewerken
    // bent, want dan moet zijn naam wel zichtbaar blijven.
    const adviceUnavailable = adviceGroups.length === 0 && !isAdviceForm

    const materialSearchHandler = (e) => setMaterialSearch(e.target.value)
    const adviceSearchHandler = (e) => setAdviceSearch(e.target.value)

    const onNameChange = (e) => {
        const value = e.target.value

        // De bestaande naam opnieuw kiezen verandert niets.
        if (value.startsWith(EXISTING_PREFIX)) return

        // 'Staat niet in de lijst': geen standaardnaam, dus ook geen
        // automatische koppeling. De eigen naam wordt de naam en 'Hoort bij' is
        // de enige manier om dit mee te laten tellen. Wat er al getypt was blijft
        // staan -- de gebruiker zegt hier alleen dat zijn product er niet bij zit.
        if (value === CUSTOM_VALUE) {
            setForm((prev) => ({
                ...prev,
                Kind: MATERIAL,
                Title: '',
                Custom: true,
                InterventionCode: '',
            }))
            return
        }

        const option = findCatalogOption(catalogGroups, value)
        if (!option) {
            setForm((prev) => ({
                ...prev,
                Kind: MATERIAL,
                Title: '',
                DisplayTitle: '',
                ArticleNumber: '',
                InterventionCode: '',
                Custom: false,
            }))
            return
        }

        const advice = option.kind === ADVICE

        setForm((prev) => ({
            ...prev,
            Kind: option.kind,
            Title: option.title,
            Category: option.category,
            Custom: false,
            // Een eigen naam hoort bij een product, niet bij het formulier. Een
            // ander product kiezen laat hem dus niet meereizen: anders draagt
            // een LED-lamp ineens de naam die bij tochtband hoorde.
            DisplayTitle: '',
            ArticleNumber: '',
            // De code komt alleen van een advies. Bij een materiaal blijft hij
            // leeg: dat wordt via de naam opgezocht.
            InterventionCode: advice ? option.code : '',
            // Bij een advies is de eenheid die van de meetstandaard en niet in
            // te vullen; voorraad en prijs bestaan er niet. Die velden worden
            // hier leeggemaakt en niet alleen verborgen, anders reist de waarde
            // van het vorige gekozen materiaal mee naar Firestore.
            Unit: option.unit || (advice ? '' : prev.Unit),
            QuantityInStock: advice ? 0 : prev.QuantityInStock,
            LowStockThreshold: advice ? 0 : prev.LowStockThreshold,
            ItemPrice: advice ? 0 : prev.ItemPrice,
            UnitsPerItem: advice ? 1 : prev.UnitsPerItem,
        }))
    }

    // What, if anything, to ask about the unit conversion. Derived from the
    // interventie this material counts towards, so the question can name the
    // registration unit the standaard actually measures in. Een coachadvies
    // wordt niet omgerekend, dus daar valt niets te vragen.
    const factorQuestion = isAdviceForm
        ? { ask: false, question: '', suffix: '', note: null }
        : materialFactorQuestion(form, library)

    // 'Hoort bij': aan welke interventie telt dit materiaal mee? Alleen voor
    // materialen — een advies krijgt zijn code uit de keuzelijst hierboven.
    // De koppeltabel blijft de terugval, dus wie standaardmateriaal invoert
    // hoeft niets te kiezen. Voor een eigen productnaam ("Radiatorfolie 40cm
    // (TZ410601)") is dit de enige manier om het mee te laten tellen.
    // AI-CONTEXT: docs/inbox.md (bug #119)
    const linkGroups = isAdviceForm ? [] : interventionOptions(library)
    const afgeleideNaam = isAdviceForm
        ? ''
        : interventionNameFor(library, defaultForMaterial(form.Title)?.code || '')

    // Een regel heeft een naam zodra er een standaardnaam is OF een eigen naam.
    // Bij 'Staat niet in de lijst' is de eigen naam de enige die er is.
    const naamOntbreekt = !form.Title.trim() && !form.DisplayTitle.trim()

    const confirm = () => {
        if (naamOntbreekt) {
            // Eerder faalde dit stil: klikken, niets gebeurt, niets zegt waarom.
            setTriedToSave(true)
            return
        }

        // Unlike the other numeric fields, an unfilled factor stays null rather
        // than falling back to 0 — see the note on emptyForm.
        const factor = Number(form.UnitsPerItem)
        // When the question is not on screen there is no input to read, so the
        // stored value is passed through untouched. Writing null here would
        // wipe a factor a colleague filled in earlier, purely because the
        // standaard happened to be unreachable this session.
        const unitsPerItem = !factorQuestion.ask
            ? (material?.UnitsPerItem ?? null)
            : Number.isFinite(factor) && factor > 0 ? factor : null

        onSave({
            ...form,
            QuantityInStock: Number(form.QuantityInStock) || 0,
            LowStockThreshold: Number(form.LowStockThreshold) || 0,
            ItemPrice: Number(form.ItemPrice) || 0,
            // Een advies wordt niet omgerekend: je registreert het meteen in de
            // eenheid van de standaard.
            UnitsPerItem: isAdviceForm ? 1 : unitsPerItem,
        })
    }

    const content = (
        <ReadOnlyGuard edit="housevisits.edit">
        <div className='materials-modal-form'>
            <div className='materials-modal-field-row materials-modal-choice-row'>
                <div className='materials-modal-field'>
                    <label htmlFor={MATERIAL_SELECT_ID}><b>Materiaal</b></label>
                    <input
                        className='materials-modal-search'
                        type='text'
                        value={materialSearch}
                        onChange={materialSearchHandler}
                        placeholder='Zoeken…'
                        aria-label='Zoek een materiaal'
                        autoComplete='off'
                    />
                    <select
                        id={MATERIAL_SELECT_ID}
                        value={materialValue}
                        onChange={onNameChange}
                        autoFocus
                    >
                        <option value=''>Kies een materiaal</option>
                        <option value={CUSTOM_VALUE}>Staat niet in de lijst</option>
                        {!isAdviceForm && existingValue && (
                            <optgroup label='Bestaande naam'>
                                <option value={existingValue}>{form.Title}</option>
                            </optgroup>
                        )}
                        {visibleMaterialGroups.map((group) => (
                            <optgroup key={group.label} label={group.category}>
                                {group.options.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.title}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                    {noMaterialMatches && (
                        <p className='materials-modal-hint'>Geen materiaal gevonden</p>
                    )}
                </div>

                <div className='materials-modal-field'>
                    <label htmlFor={ADVICE_SELECT_ID}><b>Coachadvies</b></label>
                    <input
                        className='materials-modal-search'
                        type='text'
                        value={adviceSearch}
                        onChange={adviceSearchHandler}
                        placeholder='Zoeken…'
                        aria-label='Zoek een coachadvies'
                        autoComplete='off'
                        disabled={adviceUnavailable}
                    />
                    <select
                        id={ADVICE_SELECT_ID}
                        value={adviceValue}
                        onChange={onNameChange}
                        disabled={adviceUnavailable}
                    >
                        <option value=''>
                            {adviceUnavailable ? 'Coachadviezen niet beschikbaar' : 'Kies een coachadvies'}
                        </option>
                        {isAdviceForm && existingValue && (
                            <optgroup label='Bestaande naam'>
                                <option value={existingValue}>{form.Title}</option>
                            </optgroup>
                        )}
                        {visibleAdviceGroups.map((group) => (
                            <optgroup key={group.label} label={group.category}>
                                {group.options.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.title}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                    {noAdviceMatches && (
                        <p className='materials-modal-hint'>Geen coachadvies gevonden</p>
                    )}
                </div>
            </div>

            {triedToSave && naamOntbreekt && (
                <p className='materials-modal-hint materials-modal-hint-error'>
                    Kies een materiaal of coachadvies, of typ een eigen naam.
                </p>
            )}

            <div className='materials-modal-field'>
                <label><b>Categorie</b></label>
                <input
                    type='text'
                    value={form.Category}
                    onChange={update('Category')}
                    placeholder='bv. Isolatie'
                />
            </div>

            {!isAdviceForm && (
                <div className='materials-modal-field-row'>
                    <div className='materials-modal-field'>
                        <label htmlFor={OWN_NAME_ID}><b>Eigen naam</b></label>
                        <input
                            id={OWN_NAME_ID}
                            type='text'
                            value={form.DisplayTitle}
                            onChange={update('DisplayTitle')}
                            placeholder='Laat leeg voor de standaardnaam'
                            autoComplete='off'
                        />
                    </div>

                    <div className='materials-modal-field'>
                        <label htmlFor={ARTICLE_NUMBER_ID}><b>Productnummer</b></label>
                        <input
                            id={ARTICLE_NUMBER_ID}
                            type='text'
                            value={form.ArticleNumber}
                            onChange={update('ArticleNumber')}
                            placeholder='bv. GS359219'
                            autoComplete='off'
                        />
                    </div>
                </div>
            )}

            {!isAdviceForm && (
                <div className='materials-modal-field-row'>
                    <div className='materials-modal-field'>
                        <label><b>Eenheid</b></label>
                        <input
                            type='text'
                            value={form.Unit}
                            onChange={update('Unit')}
                            list={UNIT_DATALIST_ID}
                            placeholder='bv. stuks'
                        />
                        <datalist id={UNIT_DATALIST_ID}>
                            {UNIT_OPTIONS.map((u) => <option key={u} value={u} />)}
                        </datalist>
                    </div>

                    <div className='materials-modal-field'>
                        <label><b>Voorraad</b></label>
                        <input
                            type='number'
                            min='0'
                            value={form.QuantityInStock}
                            onChange={update('QuantityInStock')}
                        />
                    </div>

                    <div className='materials-modal-field'>
                        <label><b>Laag vanaf</b></label>
                        <input
                            type='number'
                            min='0'
                            value={form.LowStockThreshold}
                            onChange={update('LowStockThreshold')}
                        />
                    </div>
                </div>
            )}

            {!isAdviceForm && (
                <div className='materials-modal-field'>
                    <label><b>Item Prijs (€)</b></label>
                    <input
                        type='number'
                        min='0'
                        step='0.01'
                        value={form.ItemPrice}
                        onChange={update('ItemPrice')}
                        placeholder='0,00'
                    />
                </div>
            )}

            {error && !isAdviceForm && (
                <p className='materials-modal-hint materials-modal-hint-error'>
                    De meetstandaard kon niet worden opgehaald, dus hier staat niet
                    wat dit materiaal oplevert.
                </p>
            )}

            {linkGroups.length > 0 && (
                <div className='materials-modal-field'>
                    <label htmlFor='materials-intervention'>
                        <b>Hoort bij</b>
                    </label>
                    <select
                        id='materials-intervention'
                        value={form.InterventionCode}
                        onChange={update('InterventionCode')}
                    >
                        <option value=''>
                            {afgeleideNaam
                                ? `Automatisch — ${afgeleideNaam}`
                                : 'Kies waar dit materiaal bij hoort'}
                        </option>
                        {linkGroups.map((group) => (
                            <optgroup key={group.label} label={group.label}>
                                {group.options.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                    {!afgeleideNaam && !form.InterventionCode && (
                        <p className='materials-modal-hint'>
                            Zonder keuze telt dit materiaal niet mee in de bespaarde CO2
                            en de maatschappelijke besparing.
                        </p>
                    )}
                </div>
            )}

            {factorQuestion.ask && (
                <div className='materials-modal-field'>
                    <label htmlFor='materials-units-per-item'>
                        <b>{factorQuestion.question}</b>
                    </label>
                    <div className='materials-modal-factor'>
                        <input
                            id='materials-units-per-item'
                            type='number'
                            min='0'
                            step='0.01'
                            value={form.UnitsPerItem}
                            onChange={update('UnitsPerItem')}
                        />
                        <span className='materials-modal-factor-unit'>{factorQuestion.suffix}</span>
                    </div>
                </div>
            )}

            {factorQuestion.note && (
                <p className='materials-modal-hint'>{factorQuestion.note}</p>
            )}

            <div className='materials-modal-field'>
                <label><b>Omschrijving</b></label>
                <textarea
                    value={form.Description}
                    onChange={update('Description')}
                    rows={3}
                />
            </div>
        </div>
        </ReadOnlyGuard>
    )

    return (
        <Modal
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            confirm={confirm}
            pageHeader={material
                ? (isAdviceForm ? 'Coachadvies bewerken' : 'Materiaal bewerken')
                : 'Materiaal of advies toevoegen'}
            content={content}
        />
    )
}

export default MaterialsModal
